"use client";

import { LANDING_CONTAINER_CLASS } from "@/components/landing/constants";
import PricelistCard from "@/components/landing/pricelist-card";
import PriceBreakdown from "@/components/landing/price-breakdown";
import { useCallback, useEffect, useRef, useState } from "react";
import { useRouter } from "next/navigation";
import { Label, Switch } from "@restomat/components/client/ui";
import { useTranslations } from "next-intl";
import { trackCalculatorUpdate } from "@/lib/analytics";
import {
  getStoredBilling,
  getStoredPackages,
  setStoredBilling,
  setStoredPackages,
} from "@/utils/landing-storage";
import { PRICING_TIERS } from "@/components/landing/pricing-data";
import { motion, useInView } from "framer-motion";
import { fadeInUp, staggerContainer } from "./animations";

/** Preselected on first view so the calculator and CTA are never empty. */
const DEFAULT_TIER_ID = "maxipes";

/**
 * Landing page pricing calculator.
 * Read-only for visitors — no checkout. For the admin checkout version, see AdminPricingSection.
 *
 * Since #609 the tiers are nested, so this is a single choice rather than a multi-select with a
 * conflict matrix: picking a tier replaces the previous one.
 */
export default function PricingSection() {
  const t = useTranslations("landing.pricing");
  const router = useRouter();
  const [billingCycle, setBillingCycle] = useState<"monthly" | "yearly">(
    "yearly",
  );
  const [selectedTierId, setSelectedTierId] = useState<string>(DEFAULT_TIER_ID);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const stored = getStoredPackages();
    const restored = stored.find((id) => PRICING_TIERS.some((t) => t.id === id));
    if (restored) {
      setSelectedTierId(restored);
    } else {
      setStoredPackages([DEFAULT_TIER_ID]);
    }

    setBillingCycle(getStoredBilling());
  }, []);

  const updateUrlParams = useCallback(
    (tierId: string, billing: "monthly" | "yearly") => {
      const params = new URLSearchParams();
      params.set("packages", tierId);
      params.set("billing", billing);
      router.replace(`?${params.toString()}#pricing`, { scroll: false });
    },
    [router],
  );

  const handleSelectTier = (id: string) => {
    setSelectedTierId(id);
    setStoredPackages([id]);
    updateUrlParams(id, billingCycle);

    const tier = PRICING_TIERS.find((p) => p.id === id);
    if (tier) {
      trackCalculatorUpdate(tier.price[billingCycle], [t(tier.titleKey)]);
    }
  };

  const updateBillingCycle = useCallback(
    (cycle: "monthly" | "yearly") => {
      setBillingCycle(cycle);
      setStoredBilling(cycle);
      updateUrlParams(selectedTierId, cycle);
    },
    [selectedTierId, updateUrlParams],
  );

  const selectedTier = PRICING_TIERS.find((p) => p.id === selectedTierId);

  const headerRef = useRef(null);
  const headerInView = useInView(headerRef, { once: true, amount: "some" });

  return (
    <section id="pricing" className="py-20">
      <div className={`space-y-12 ${LANDING_CONTAINER_CLASS}`}>
        <motion.header
          ref={headerRef}
          className="flex flex-col lg:flex-row items-center text-center lg:text-left gap-4 lg:gap-16 justify-between"
          initial="hidden"
          animate={headerInView ? "visible" : "hidden"}
          variants={staggerContainer}
        >
          <div className="flex flex-col lg:flex-row items-center gap-4 lg:gap-16">
            <motion.h2 variants={fadeInUp}>{t("heading")}</motion.h2>
            <motion.div
              className={"text-leading-md-black max-w-3xl"}
              variants={fadeInUp}
            >
              {t("subheading")}
            </motion.div>
          </div>
        </motion.header>
        <div className={"w-full flex flex-col gap-8"}>
          <div className={"flex items-end flex-col gap-2"}>
            <div className={"flex gap-3 mr-8"}>
              <span className={"font-bold -mt-3 text-primary"}>
                {t("cheaperPrices")}
              </span>
              <svg
                width="56"
                height="53"
                viewBox="0 0 56 53"
                fill="none"
                className={"-scale-x-100"}
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M53.9045 0.112305C53.6125 0.117648 50.9457 0.370419 46.9661 1.43945C42.9402 2.52092 37.5649 4.43859 31.9358 7.7832L31.011 8.35156C21.4457 14.3697 9.9581 25.7438 5.88013 46.6182L2.5061 38.6387L2.50317 38.6309C2.2119 38.0681 1.51567 37.6649 0.896729 37.9639C0.31201 38.2464 -0.0993453 38.919 0.203369 39.6055L5.17114 51.5205C5.17126 51.554 5.17234 51.5944 5.19263 51.6338C5.20449 51.6567 5.22031 51.676 5.2356 51.6924C5.24488 51.7024 5.25663 51.7127 5.2688 51.7246C5.27396 51.7465 5.2817 51.767 5.29419 51.7852C5.31491 51.8152 5.3414 51.8345 5.3645 51.8457C5.36789 51.8473 5.37193 51.8473 5.37524 51.8486C5.37634 51.851 5.37597 51.8541 5.3772 51.8564C5.38922 51.8797 5.40821 51.9061 5.43872 51.9258C5.46966 51.9457 5.5064 51.955 5.54614 51.9551C5.54955 51.9551 5.5474 51.9512 5.5686 51.9717C5.58914 51.9915 5.62722 52.0263 5.69067 52.0381C5.79012 52.1216 5.94556 52.199 6.11255 52.2178L6.19165 52.2227H6.37524C6.67736 52.2227 6.97949 52.1232 7.27466 51.8379V51.8369L16.762 42.9355L16.7629 42.9346C17.2642 42.4501 17.2642 41.672 16.7629 41.1875C16.2645 40.706 15.4685 40.7049 14.969 41.1846L8.27954 47.1328C12.194 26.4707 23.5159 15.3278 33.9368 9.34668C39.1725 6.34159 44.1816 4.63883 47.9104 3.6875C49.7745 3.21192 51.3187 2.92464 52.4104 2.75586C52.9562 2.67147 53.3895 2.61652 53.6926 2.58301C53.8438 2.56629 53.9627 2.55511 54.0471 2.54785C54.0894 2.54422 54.1233 2.54169 54.1477 2.54004C54.1599 2.53922 54.1696 2.53846 54.177 2.53809C54.1806 2.5379 54.1836 2.53816 54.1858 2.53809C54.187 2.53805 54.1879 2.53713 54.1887 2.53711H54.1975L54.2053 2.53613C54.5058 2.48773 54.8028 2.31988 55.0129 2.08105C55.2238 1.84128 55.3531 1.52313 55.302 1.17676C55.2025 0.503541 54.6044 0.0183268 53.9045 0.112305Z"
                  fill="#0C0D0D"
                  stroke="#0C0D0D"
                  strokeWidth="0.2"
                />
              </svg>
            </div>

            <div className="flex items-center justify-end gap-4">
              <Label
                htmlFor="billing-switch"
                className={`cursor-pointer ${billingCycle === "monthly" ? "text-foreground font-bold" : "text-muted-foreground"}`}
              >
                {t("monthly")}
              </Label>
              <Switch
                size={"lg"}
                variant={"bold"}
                id="billing-switch"
                checked={billingCycle === "yearly"}
                onCheckedChange={(checked) =>
                  updateBillingCycle(checked ? "yearly" : "monthly")
                }
              />
              <Label
                htmlFor="billing-switch"
                className={`cursor-pointer ${billingCycle === "yearly" ? "text-foreground font-bold" : "text-muted-foreground"}`}
              >
                {t("yearly")}
              </Label>
            </div>
          </div>
          <div
            className={
              "grid gap-8 grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 items-stretch"
            }
          >
            {PRICING_TIERS.map((tier) => (
              <PricelistCard
                key={tier.id}
                tierId={tier.id}
                className={"flex-1 h-full"}
                title={t(tier.titleKey)}
                description={t(tier.descriptionKey)}
                features={tier.featureKeys.map((key) => t(key))}
                highlightBadge={
                  tier.highlightBadgeKey ? t(tier.highlightBadgeKey) : undefined
                }
                isFree={tier.price.monthly === 0}
                price={Math.round(
                  tier.price[billingCycle] /
                    (billingCycle === "yearly" ? 12 : 1),
                )}
                selected={selectedTierId === tier.id}
                onClick={() => handleSelectTier(tier.id)}
              />
            ))}
            <PriceBreakdown
              className={"xl:col-span-3 lg:w-full xl:w-1/3 lg:justify-self-end"}
              selectedTier={
                selectedTier
                  ? { ...selectedTier, title: t(selectedTier.titleKey) }
                  : undefined
              }
              billingCycle={billingCycle}
            />
          </div>
        </div>
      </div>
    </section>
  );
}
